import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { api, errorText } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { SkeletonList } from '../components/ui/Skeleton';
import ErrorState from '../components/ui/ErrorState';
import Modal from '../components/ui/Modal';
import {
  Building2,
  Plus,
  Pencil,
  Trash2,
  Play,
  RefreshCw,
  Globe,
  MapPin,
  ExternalLink,
  Download,
  Filter,
  Crown
} from 'lucide-react';

interface Company {
  id: number;
  name: string;
  website: string | null;
  career_page_url: string | null;
  ats_type: string | null;
  ats_slug: string | null;
  industry: string | null;
  size: string | null;
  location: string | null;
  fortune_500_rank: number | null;
  category: string | null;
  created_at: string;
  updated_at: string;
}

function canScrapeCompany(company: Company): boolean {
  return Boolean((company.ats_type && company.ats_slug) || company.career_page_url);
}

interface CompanyForm {
  name: string;
  website: string;
  career_page_url: string;
  ats_type: string;
  ats_slug: string;
  industry: string;
  size: string;
  location: string;
  category: string;
}

const emptyForm: CompanyForm = {
  name: '', website: '', career_page_url: '', ats_type: '',
  ats_slug: '', industry: '', size: '', location: '', category: 'Other'
};

const CATEGORY_COLORS: Record<string, string> = {
  'Fortune 500 Tech': 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  'AI': 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300',
  'Fintech': 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  'Enterprise SaaS': 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300',
  'Developer Tools': 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300',
  'Defense Tech': 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  'Health Tech': 'bg-pink-100 text-pink-800 dark:bg-pink-900/40 dark:text-pink-300',
  'Data/AI': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
  'Design': 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/40 dark:text-fuchsia-300',
  'Productivity': 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300',
  'FMCG': 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  'Food & Beverage': 'bg-lime-100 text-lime-800 dark:bg-lime-900/40 dark:text-lime-300',
  'Retail': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  'D2C / Consumer': 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300',
  'GovTech': 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300',
  'Federal Contractor': 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300',
  'Civic Tech': 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  'US Government': 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300',
  'UK Government': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
  'Canada Government': 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  'Australia Government': 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  'Other': 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300',
};

const Companies: React.FC = () => {
  const { isAdmin } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<CompanyForm>(emptyForm);
  const [scraping, setScraping] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [bulkLoading, setBulkLoading] = useState(false);
  const { toast } = useToast();
  const { confirm } = useConfirm();

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = selectedCategory !== 'all' ? `?category=${encodeURIComponent(selectedCategory)}` : '';
      const data = await api.get<{ companies?: Company[]; categories?: string[] }>(
        `/api/companies${params}`,
        { retry: 2 },
      );
      setCompanies(data.companies || []);
      if (data.categories) setCategories(data.categories);
    } catch (e) {
      setLoadError(errorText(e, 'Failed to load companies'));
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (c: Company) => {
    setEditingId(c.id);
    setForm({
      name: c.name,
      website: c.website || '',
      career_page_url: c.career_page_url || '',
      ats_type: c.ats_type || '',
      ats_slug: c.ats_slug || '',
      industry: c.industry || '',
      size: c.size || '',
      location: c.location || '',
      category: c.category || 'Other',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editingId) await api.put(`/api/companies/${editingId}`, form);
      else await api.post('/api/companies', form);
      setShowModal(false);
      await fetchCompanies();
      toast.success(editingId ? 'Company updated' : 'Company added');
    } catch (e) {
      toast.error(errorText(e, 'Failed to save company'));
    } finally {
      setSaving(false);
    }
  };

  const handleDetect = async () => {
    const url = form.career_page_url.trim();
    if (!url) { toast.error('Enter a career page URL first'); return; }
    setDetecting(true);
    try {
      const d = await api.post<{ ats_type: string; ats_slug: string | null; crawlable: boolean; reason: string }>(
        '/api/companies/detect',
        { url },
      );
      if (d.crawlable && d.ats_slug) {
        setForm((f) => ({ ...f, ats_type: d.ats_type, ats_slug: d.ats_slug ?? '' }));
        toast.success(`Detected ${d.ats_type} (${d.ats_slug})`);
      } else {
        toast.info(d.reason || 'No crawlable ATS detected from that URL');
      }
    } catch (e) {
      toast.error(errorText(e, 'Detection failed'));
    } finally {
      setDetecting(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    const ok = await confirm({
      title: 'Delete company',
      message: `Delete ${name}? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.del(`/api/companies/${id}`);
      await fetchCompanies();
      toast.success('Company deleted');
    } catch (e) {
      toast.error(errorText(e, 'Failed to delete company'));
    }
  };

  const handleScrape = async (id: number) => {
    setScraping(id);
    try {
      await api.post('/api/scrape', { company_id: id });
      await fetchCompanies();
    } catch (e) {
      toast.error(errorText(e, 'Scraping failed'));
    } finally {
      setScraping(null);
    }
  };

  const handleBulkImport = async (pack: 'fortune500' | 'fmcg') => {
    const label = pack === 'fortune500' ? 'Fortune 500 + tech' : 'FMCG / D2C consumer';
    const data_pack = pack === 'fortune500' ? FORTUNE_500_PACK : FMCG_PACK;
    const ok = await confirm({
      title: 'Load company pack',
      message: `Load ${label} companies? This will add ~${data_pack.length} companies.`,
      confirmLabel: 'Load',
    });
    if (!ok) return;
    setBulkLoading(true);
    try {
      const data = await api.post<{ inserted: number; skipped: number }>('/api/companies/bulk', {
        companies: data_pack,
      });
      toast.success(`Imported ${data.inserted} companies (${data.skipped} already existed)`);
      await fetchCompanies();
    } catch (e) {
      toast.error(errorText(e, 'Bulk import failed'));
    } finally {
      setBulkLoading(false);
    }
  };

  const atsBadgeColor = (type: string | null) => {
    if (type === 'greenhouse') return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
    if (type === 'lever') return 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300';
    if (type === 'ashby') return 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300';
    if (type === 'smartrecruiters') return 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300';
    if (type === 'workable') return 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300';
    if (type === 'recruitee') return 'bg-pink-100 text-pink-800 dark:bg-pink-900/40 dark:text-pink-300';
    if (type === 'personio') return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300';
    if (type === 'themuse' || type === 'arbeitnow' || type === 'remotive') return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300';
    return 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-slate-300';
  };

  const categoryColor = (cat: string | null) => {
    return CATEGORY_COLORS[cat || 'Other'] || CATEGORY_COLORS['Other'];
  };

  // Group companies by category
  const grouped = companies.reduce<Record<string, Company[]>>((acc, co) => {
    const cat = co.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(co);
    return acc;
  }, {});

  const CATEGORY_ORDER = ['Fortune 500 Tech', 'AI', 'FMCG', 'Food & Beverage', 'Retail', 'D2C / Consumer'];
  const sortedCategories = Object.keys(grouped).sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a);
    const bi = CATEGORY_ORDER.indexOf(b);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.localeCompare(b);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center space-x-3">
          <Building2 className="w-8 h-8 text-primary-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Companies</h1>
            <p className="text-gray-600 dark:text-slate-400 mt-1">
              {companies.length} companies tracked
              {selectedCategory !== 'all' && ` in ${selectedCategory}`}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {isAdmin && (
            <button
              onClick={() => handleBulkImport('fortune500')}
              disabled={bulkLoading}
              className="flex items-center px-4 py-2 bg-amber-600 text-white rounded-xl hover:bg-amber-700 disabled:opacity-50"
            >
              {bulkLoading ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Importing...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" />Fortune 500</>
              )}
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => handleBulkImport('fmcg')}
              disabled={bulkLoading}
              className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-xl hover:bg-orange-700 disabled:opacity-50"
            >
              {bulkLoading ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Importing...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" />FMCG Pack</>
              )}
            </button>
          )}
          {isAdmin && (
            <button
              onClick={openAdd}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Company
            </button>
          )}
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex items-center space-x-2 flex-wrap gap-2">
        <Filter className="w-4 h-4 text-gray-500 dark:text-slate-400" />
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
            selectedCategory === 'all'
              ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
              : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600'
          }`}
        >
          All
        </button>
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat === selectedCategory ? 'all' : cat)}
            className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
              selectedCategory === cat
                ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                : `${categoryColor(cat)} hover:opacity-80`
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Companies Grid (grouped) */}
      {loading ? (
        <SkeletonList rows={5} />
      ) : loadError ? (
        <ErrorState message={loadError} onRetry={fetchCompanies} />
      ) : companies.length === 0 ? (
        <div className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-12 text-center">
          <Building2 className="w-16 h-16 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No companies yet</h3>
          <p className="text-gray-500 dark:text-slate-400 mb-4">Add a company or load the Fortune 500 pack to get started</p>
          {isAdmin && (
            <div className="flex justify-center space-x-3">
              <button onClick={() => handleBulkImport('fortune500')} className="px-4 py-2 bg-amber-600 text-white rounded-xl hover:bg-amber-700">
                Load Fortune 500 Pack
              </button>
              <button onClick={() => handleBulkImport('fmcg')} className="px-4 py-2 bg-orange-600 text-white rounded-xl hover:bg-orange-700">
                Load FMCG Pack
              </button>
              <button onClick={openAdd} className="px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700">
                Add Manually
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {sortedCategories.map((cat) => (
            <div key={cat}>
              <div className="flex items-center space-x-2 mb-4">
                {cat === 'Fortune 500 Tech' && <Crown className="w-5 h-5 text-amber-500" />}
                <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-200">{cat}</h2>
                <span className="text-sm text-gray-500 dark:text-slate-400">({grouped[cat].length})</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {grouped[cat].map((company, index) => (
                  <motion.div
                    key={company.id}
                    className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 hover:shadow-md transition-shadow"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                  >
                    <div className="p-5">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">{company.name}</h3>
                            {company.fortune_500_rank && (
                              <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">#{company.fortune_500_rank}</span>
                            )}
                          </div>
                          {company.location && (
                            <p className="text-xs text-gray-500 dark:text-slate-400 flex items-center mt-1">
                              <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                              {company.location}
                            </p>
                          )}
                        </div>
                        {isAdmin && (
                          <div className="flex space-x-1 ml-2">
                            <button onClick={() => openEdit(company)} className="p-1.5 text-gray-400 dark:text-slate-500 hover:text-primary-600 rounded">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => handleDelete(company.id, company.name)} className="p-1.5 text-gray-400 dark:text-slate-500 hover:text-red-600 rounded">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {company.ats_type && (
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${atsBadgeColor(company.ats_type)}`}>
                            {company.ats_type}
                          </span>
                        )}
                        {!company.ats_type && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400">
                            No ATS
                          </span>
                        )}
                        {company.industry && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300">
                            {company.industry}
                          </span>
                        )}
                        {company.size && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300">
                            {company.size}
                          </span>
                        )}
                      </div>

                      {company.career_page_url && (
                        <a
                          href={company.career_page_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 flex items-center mb-3 truncate"
                        >
                          <Globe className="w-3 h-3 mr-1 flex-shrink-0" />
                          <span className="truncate">{company.career_page_url}</span>
                          <ExternalLink className="w-3 h-3 ml-1 flex-shrink-0" />
                        </a>
                      )}

                      {isAdmin && (
                        <div className="pt-2 border-t border-gray-100 dark:border-slate-700">
                          <button
                            onClick={() => handleScrape(company.id)}
                            disabled={scraping === company.id || !canScrapeCompany(company)}
                            className="w-full flex items-center justify-center px-3 py-1.5 text-xs bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 rounded-xl hover:bg-primary-100 dark:hover:bg-primary-900/40 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {scraping === company.id ? (
                              <><RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" />Scraping...</>
                            ) : !canScrapeCompany(company) ? (
                              <span className="text-gray-400 dark:text-slate-500">Manual tracking only</span>
                            ) : !company.ats_type ? (
                              <><Play className="w-3.5 h-3.5 mr-1" />Detect & Scrape</>
                            ) : (
                              <><Play className="w-3.5 h-3.5 mr-1" />Scrape Jobs</>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingId ? 'Edit Company' : 'Add Company'}
        footer={
          <>
            <button
              onClick={() => setShowModal(false)}
              className="rounded-xl bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !form.name.trim()}
              className="rounded-xl bg-primary-600 px-4 py-2 text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : editingId ? 'Update' : 'Add Company'}
            </button>
          </>
        }
      >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Company Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-slate-800 dark:text-white dark:placeholder-slate-500"
                  placeholder="e.g. Anthropic"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">ATS Type</label>
                  <select
                    value={form.ats_type}
                    onChange={(e) => setForm({ ...form, ats_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-slate-800 dark:text-white"
                  >
                    <option value="">Select ATS</option>
                    <option value="greenhouse">Greenhouse</option>
                    <option value="lever">Lever</option>
                    <option value="ashby">Ashby</option>
                    <option value="smartrecruiters">SmartRecruiters</option>
                    <option value="workable">Workable</option>
                    <option value="recruitee">Recruitee</option>
                    <option value="personio">Personio</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">ATS Slug</label>
                  <input
                    type="text"
                    value={form.ats_slug}
                    onChange={(e) => setForm({ ...form, ats_slug: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-slate-800 dark:text-white dark:placeholder-slate-500"
                    placeholder="e.g. anthropic"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Career Page URL</label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={form.career_page_url}
                    onChange={(e) => setForm({ ...form, career_page_url: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-slate-800 dark:text-white dark:placeholder-slate-500"
                    placeholder="https://jobs.smartrecruiters.com/BoschGroup"
                  />
                  <button
                    type="button"
                    onClick={handleDetect}
                    disabled={detecting || !form.career_page_url.trim()}
                    className="whitespace-nowrap px-3 py-2 text-sm font-medium rounded-xl bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/40 disabled:opacity-50"
                  >
                    {detecting ? 'Detecting…' : 'Detect ATS'}
                  </button>
                </div>
                <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
                  Paste a Greenhouse / Lever / Ashby / SmartRecruiters / Workable careers URL and click Detect to auto-fill the ATS type &amp; slug.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Website</label>
                <input
                  type="url"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-slate-800 dark:text-white dark:placeholder-slate-500"
                  placeholder="https://www.anthropic.com"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-slate-800 dark:text-white"
                  >
                    <option value="Other">Other</option>
                    <option value="Fortune 500 Tech">Fortune 500 Tech</option>
                    <option value="AI">AI</option>
                    <option value="Fintech">Fintech</option>
                    <option value="Enterprise SaaS">Enterprise SaaS</option>
                    <option value="Developer Tools">Developer Tools</option>
                    <option value="Defense Tech">Defense Tech</option>
                    <option value="Health Tech">Health Tech</option>
                    <option value="Data/AI">Data/AI</option>
                    <option value="Design">Design</option>
                    <option value="Productivity">Productivity</option>
                    <option value="FMCG">FMCG</option>
                    <option value="Food & Beverage">Food & Beverage</option>
                    <option value="Retail">Retail</option>
                    <option value="D2C / Consumer">D2C / Consumer</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Industry</label>
                  <input
                    type="text"
                    value={form.industry}
                    onChange={(e) => setForm({ ...form, industry: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-slate-800 dark:text-white dark:placeholder-slate-500"
                    placeholder="e.g. AI"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Size</label>
                  <select
                    value={form.size}
                    onChange={(e) => setForm({ ...form, size: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-slate-800 dark:text-white"
                  >
                    <option value="">Select size</option>
                    <option value="Small">Small</option>
                    <option value="Mid">Mid</option>
                    <option value="Large">Large</option>
                    <option value="Enterprise">Enterprise</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Location</label>
                  <input
                    type="text"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-slate-800 dark:text-white dark:placeholder-slate-500"
                    placeholder="e.g. San Francisco, CA"
                  />
                </div>
              </div>
            </div>

      </Modal>
    </div>
  );
};

// Fortune 500 + high-paying tech company pack for bulk import
const FORTUNE_500_PACK = [
  { name: 'Netflix', website: 'https://www.netflix.com', career_page_url: 'https://jobs.lever.co/netflix', ats_type: 'lever', ats_slug: 'netflix', industry: 'Entertainment/Tech', size: 'Large', location: 'Los Gatos, CA', category: 'Fortune 500 Tech' },
  { name: 'Uber', website: 'https://www.uber.com', career_page_url: 'https://boards.greenhouse.io/uber', ats_type: 'greenhouse', ats_slug: 'uber', industry: 'Transportation', size: 'Large', location: 'San Francisco, CA', category: 'Fortune 500 Tech' },
  { name: 'Airbnb', website: 'https://www.airbnb.com', career_page_url: 'https://boards.greenhouse.io/airbnb', ats_type: 'greenhouse', ats_slug: 'airbnb', industry: 'Travel/Tech', size: 'Large', location: 'San Francisco, CA', category: 'Fortune 500 Tech' },
  { name: 'Block', website: 'https://block.xyz', career_page_url: 'https://boards.greenhouse.io/block', ats_type: 'greenhouse', ats_slug: 'block', industry: 'Fintech', size: 'Large', location: 'San Francisco, CA', category: 'Fortune 500 Tech' },
  { name: 'Snowflake', website: 'https://www.snowflake.com', career_page_url: 'https://boards.greenhouse.io/snowflake', ats_type: 'greenhouse', ats_slug: 'snowflake', industry: 'Data/Cloud', size: 'Large', location: 'Bozeman, MT', category: 'Fortune 500 Tech' },
  { name: 'Palantir', website: 'https://www.palantir.com', career_page_url: 'https://boards.greenhouse.io/palantir', ats_type: 'greenhouse', ats_slug: 'palantir', industry: 'Data Analytics', size: 'Large', location: 'Denver, CO', category: 'Fortune 500 Tech' },
  { name: 'Coinbase', website: 'https://www.coinbase.com', career_page_url: 'https://boards.greenhouse.io/coinbase', ats_type: 'greenhouse', ats_slug: 'coinbase', industry: 'Crypto/Fintech', size: 'Large', location: 'San Francisco, CA', category: 'Fortune 500 Tech' },
  { name: 'DoorDash', website: 'https://www.doordash.com', career_page_url: 'https://boards.greenhouse.io/doordash', ats_type: 'greenhouse', ats_slug: 'doordash', industry: 'Delivery/Tech', size: 'Large', location: 'San Francisco, CA', category: 'Fortune 500 Tech' },
  { name: 'Cloudflare', website: 'https://www.cloudflare.com', career_page_url: 'https://boards.greenhouse.io/cloudflare', ats_type: 'greenhouse', ats_slug: 'cloudflare', industry: 'Infrastructure', size: 'Large', location: 'San Francisco, CA', category: 'Fortune 500 Tech' },
  { name: 'Apple', website: 'https://www.apple.com', career_page_url: 'https://jobs.apple.com', industry: 'Consumer Tech', size: 'Enterprise', location: 'Cupertino, CA', category: 'Fortune 500 Tech' },
  { name: 'Amazon', website: 'https://www.amazon.com', career_page_url: 'https://www.amazon.jobs', industry: 'E-Commerce/Cloud', size: 'Enterprise', location: 'Seattle, WA', category: 'Fortune 500 Tech' },
  { name: 'Google', website: 'https://www.google.com', career_page_url: 'https://careers.google.com', industry: 'Search/Cloud/AI', size: 'Enterprise', location: 'Mountain View, CA', category: 'Fortune 500 Tech' },
  { name: 'Microsoft', website: 'https://www.microsoft.com', career_page_url: 'https://careers.microsoft.com', industry: 'Enterprise Software', size: 'Enterprise', location: 'Redmond, WA', category: 'Fortune 500 Tech' },
  { name: 'Meta', website: 'https://about.meta.com', career_page_url: 'https://www.metacareers.com', industry: 'Social Media', size: 'Enterprise', location: 'Menlo Park, CA', category: 'Fortune 500 Tech' },
  { name: 'Salesforce', website: 'https://www.salesforce.com', career_page_url: 'https://careers.salesforce.com', industry: 'CRM/SaaS', size: 'Enterprise', location: 'San Francisco, CA', category: 'Fortune 500 Tech' },
  { name: 'Nvidia', website: 'https://www.nvidia.com', career_page_url: 'https://nvidia.wd5.myworkdayjobs.com/NVIDIAExternalCareerSite', industry: 'Semiconductors/AI', size: 'Enterprise', location: 'Santa Clara, CA', category: 'Fortune 500 Tech' },
  { name: 'Tesla', website: 'https://www.tesla.com', career_page_url: 'https://www.tesla.com/careers/search', industry: 'EV/Energy', size: 'Enterprise', location: 'Austin, TX', category: 'Fortune 500 Tech' },
  { name: 'Hugging Face', website: 'https://huggingface.co', career_page_url: 'https://boards.greenhouse.io/huggingface', ats_type: 'greenhouse', ats_slug: 'huggingface', industry: 'AI/ML', size: 'Mid', location: 'New York, NY', category: 'AI' },
  { name: 'Mistral AI', website: 'https://mistral.ai', career_page_url: 'https://jobs.lever.co/mistral', ats_type: 'lever', ats_slug: 'mistral', industry: 'AI/ML', size: 'Small', location: 'Paris, France', category: 'AI' },
  { name: 'Runway', website: 'https://runwayml.com', career_page_url: 'https://boards.greenhouse.io/runwayml', ats_type: 'greenhouse', ats_slug: 'runwayml', industry: 'AI/Creative', size: 'Small', location: 'New York, NY', category: 'AI' },
  { name: 'Together AI', website: 'https://www.together.ai', career_page_url: 'https://boards.greenhouse.io/togetherai', ats_type: 'greenhouse', ats_slug: 'togetherai', industry: 'AI/Infrastructure', size: 'Small', location: 'San Francisco, CA', category: 'AI' },
  { name: 'Groq', website: 'https://groq.com', career_page_url: 'https://boards.greenhouse.io/groq', ats_type: 'greenhouse', ats_slug: 'groq', industry: 'AI/Hardware', size: 'Small', location: 'Mountain View, CA', category: 'AI' },
  { name: 'xAI', website: 'https://x.ai', career_page_url: 'https://boards.greenhouse.io/xai', ats_type: 'greenhouse', ats_slug: 'xai', industry: 'AI', size: 'Mid', location: 'San Francisco, CA', category: 'AI' },
  { name: 'Plaid', website: 'https://plaid.com', career_page_url: 'https://boards.greenhouse.io/plaid', ats_type: 'greenhouse', ats_slug: 'plaid', industry: 'Fintech', size: 'Mid', location: 'San Francisco, CA', category: 'Fintech' },
  { name: 'Brex', website: 'https://brex.com', career_page_url: 'https://boards.greenhouse.io/brex', ats_type: 'greenhouse', ats_slug: 'brex', industry: 'Fintech', size: 'Mid', location: 'San Francisco, CA', category: 'Fintech' },
  { name: 'Affirm', website: 'https://www.affirm.com', career_page_url: 'https://boards.greenhouse.io/affirm', ats_type: 'greenhouse', ats_slug: 'affirm', industry: 'Fintech', size: 'Large', location: 'San Francisco, CA', category: 'Fintech' },
  { name: 'Robinhood', website: 'https://robinhood.com', career_page_url: 'https://boards.greenhouse.io/robinhood', ats_type: 'greenhouse', ats_slug: 'robinhood', industry: 'Fintech', size: 'Large', location: 'Menlo Park, CA', category: 'Fintech' },
  { name: 'Datadog', website: 'https://www.datadoghq.com', career_page_url: 'https://boards.greenhouse.io/datadog', ats_type: 'greenhouse', ats_slug: 'datadog', industry: 'Observability', size: 'Large', location: 'New York, NY', category: 'Enterprise SaaS' },
  { name: 'Twilio', website: 'https://www.twilio.com', career_page_url: 'https://boards.greenhouse.io/twilio', ats_type: 'greenhouse', ats_slug: 'twilio', industry: 'Communications', size: 'Large', location: 'San Francisco, CA', category: 'Enterprise SaaS' },
  { name: 'Confluent', website: 'https://www.confluent.io', career_page_url: 'https://boards.greenhouse.io/confluent', ats_type: 'greenhouse', ats_slug: 'confluent', industry: 'Data Streaming', size: 'Large', location: 'Mountain View, CA', category: 'Enterprise SaaS' },
  { name: 'GitLab', website: 'https://about.gitlab.com', career_page_url: 'https://boards.greenhouse.io/gitlab', ats_type: 'greenhouse', ats_slug: 'gitlab', industry: 'DevOps', size: 'Large', location: 'Remote', category: 'Enterprise SaaS' },
  { name: 'MongoDB', website: 'https://www.mongodb.com', career_page_url: 'https://boards.greenhouse.io/mongodb', ats_type: 'greenhouse', ats_slug: 'mongodb', industry: 'Database', size: 'Large', location: 'New York, NY', category: 'Enterprise SaaS' },
  { name: 'CrowdStrike', website: 'https://www.crowdstrike.com', career_page_url: 'https://boards.greenhouse.io/crowdstrike', ats_type: 'greenhouse', ats_slug: 'crowdstrike', industry: 'Cybersecurity', size: 'Large', location: 'Austin, TX', category: 'Enterprise SaaS' },
  { name: 'Supabase', website: 'https://supabase.com', career_page_url: 'https://boards.greenhouse.io/supabase', ats_type: 'greenhouse', ats_slug: 'supabase', industry: 'Developer Tools', size: 'Small', location: 'Remote', category: 'Developer Tools' },
  { name: 'Retool', website: 'https://retool.com', career_page_url: 'https://boards.greenhouse.io/retool', ats_type: 'greenhouse', ats_slug: 'retool', industry: 'Developer Tools', size: 'Mid', location: 'San Francisco, CA', category: 'Developer Tools' },
  { name: 'Grafana Labs', website: 'https://grafana.com', career_page_url: 'https://boards.greenhouse.io/grafanalabs', ats_type: 'greenhouse', ats_slug: 'grafanalabs', industry: 'Observability', size: 'Mid', location: 'New York, NY', category: 'Developer Tools' },
  { name: 'Shield AI', website: 'https://www.shield.ai', career_page_url: 'https://boards.greenhouse.io/shieldai', ats_type: 'greenhouse', ats_slug: 'shieldai', industry: 'Defense AI', size: 'Mid', location: 'San Diego, CA', category: 'Defense Tech' },
  { name: 'SpaceX', website: 'https://www.spacex.com', career_page_url: 'https://boards.greenhouse.io/spacex', ats_type: 'greenhouse', ats_slug: 'spacex', industry: 'Aerospace', size: 'Large', location: 'Hawthorne, CA', category: 'Defense Tech' },
  { name: 'Tempus AI', website: 'https://www.tempus.com', career_page_url: 'https://boards.greenhouse.io/tempus', ats_type: 'greenhouse', ats_slug: 'tempus', industry: 'Health AI', size: 'Mid', location: 'Chicago, IL', category: 'Health Tech' },
];

// FMCG / D2C consumer brands pack (scrapable ones with Greenhouse/Lever/Ashby)
const FMCG_PACK = [
  { name: 'Glossier', website: 'https://www.glossier.com', career_page_url: 'https://boards.greenhouse.io/glossier', ats_type: 'greenhouse', ats_slug: 'glossier', industry: 'Beauty / D2C', size: 'Mid', location: 'New York, NY', category: 'D2C / Consumer' },
  { name: 'Allbirds', website: 'https://www.allbirds.com', career_page_url: 'https://boards.greenhouse.io/allbirds', ats_type: 'greenhouse', ats_slug: 'allbirds', industry: 'Footwear / D2C', size: 'Mid', location: 'San Francisco, CA', category: 'D2C / Consumer' },
  { name: 'Warby Parker', website: 'https://www.warbyparker.com', career_page_url: 'https://boards.greenhouse.io/warbyparker', ats_type: 'greenhouse', ats_slug: 'warbyparker', industry: 'Eyewear / D2C', size: 'Large', location: 'New York, NY', category: 'D2C / Consumer' },
  { name: 'Beyond Meat', website: 'https://www.beyondmeat.com', career_page_url: 'https://boards.greenhouse.io/beyondmeat', ats_type: 'greenhouse', ats_slug: 'beyondmeat', industry: 'Food / Plant-Based', size: 'Mid', location: 'El Segundo, CA', category: 'D2C / Consumer' },
  { name: 'Impossible Foods', website: 'https://impossiblefoods.com', career_page_url: 'https://boards.greenhouse.io/impossiblefoods', ats_type: 'greenhouse', ats_slug: 'impossiblefoods', industry: 'Food / Plant-Based', size: 'Mid', location: 'Redwood City, CA', category: 'D2C / Consumer' },
  { name: 'Hims & Hers', website: 'https://www.forhims.com', career_page_url: 'https://boards.greenhouse.io/hims', ats_type: 'greenhouse', ats_slug: 'hims', industry: 'Health & Wellness', size: 'Large', location: 'San Francisco, CA', category: 'D2C / Consumer' },
  { name: 'Liquid Death', website: 'https://www.liquiddeath.com', career_page_url: 'https://boards.greenhouse.io/liquiddeath', ats_type: 'greenhouse', ats_slug: 'liquiddeath', industry: 'Beverages / D2C', size: 'Mid', location: 'Los Angeles, CA', category: 'D2C / Consumer' },
  { name: 'Oatly', website: 'https://www.oatly.com', career_page_url: 'https://boards.greenhouse.io/oatly', ats_type: 'greenhouse', ats_slug: 'oatly', industry: 'Dairy Alternative', size: 'Large', location: 'Malmo, Sweden', category: 'D2C / Consumer' },
  { name: 'Olipop', website: 'https://www.drinkolipop.com', career_page_url: 'https://boards.greenhouse.io/olipop', ats_type: 'greenhouse', ats_slug: 'olipop', industry: 'Beverages / D2C', size: 'Small', location: 'Oakland, CA', category: 'D2C / Consumer' },
  { name: 'Athletic Greens (AG1)', website: 'https://www.drinkag1.com', career_page_url: 'https://boards.greenhouse.io/athleticgreens', ats_type: 'greenhouse', ats_slug: 'athleticgreens', industry: 'Health & Wellness', size: 'Mid', location: 'Carson City, NV', category: 'D2C / Consumer' },
  { name: "Harry's", website: 'https://www.harrys.com', career_page_url: 'https://boards.greenhouse.io/harrys', ats_type: 'greenhouse', ats_slug: 'harrys', industry: 'Personal Care / D2C', size: 'Mid', location: 'New York, NY', category: 'D2C / Consumer' },
  { name: 'Dr. Squatch', website: 'https://www.drsquatch.com', career_page_url: 'https://jobs.lever.co/drsquatch', ats_type: 'lever', ats_slug: 'drsquatch', industry: 'Personal Care / D2C', size: 'Small', location: 'Los Angeles, CA', category: 'D2C / Consumer' },
  { name: 'Dollar Shave Club', website: 'https://www.dollarshaveclub.com', career_page_url: 'https://boards.greenhouse.io/dollarshaveclub', ats_type: 'greenhouse', ats_slug: 'dollarshaveclub', industry: 'Personal Care / D2C', size: 'Mid', location: 'Marina del Rey, CA', category: 'D2C / Consumer' },
  // FMCG giants (tracked, no auto-scrape)
  { name: 'Procter & Gamble', website: 'https://www.pg.com', career_page_url: 'https://www.pgcareers.com', industry: 'Consumer Goods', size: 'Enterprise', location: 'Cincinnati, OH', category: 'FMCG' },
  { name: 'Unilever', website: 'https://www.unilever.com', career_page_url: 'https://careers.unilever.com', industry: 'Consumer Goods', size: 'Enterprise', location: 'London, UK', category: 'FMCG' },
  { name: 'Nestle', website: 'https://www.nestle.com', career_page_url: 'https://www.nestle.com/jobs', industry: 'Consumer Goods', size: 'Enterprise', location: 'Vevey, Switzerland', category: 'FMCG' },
  { name: 'Coca-Cola', website: 'https://www.coca-colacompany.com', career_page_url: 'https://careers.coca-colacompany.com', industry: 'Beverages', size: 'Enterprise', location: 'Atlanta, GA', category: 'Food & Beverage' },
  { name: 'PepsiCo', website: 'https://www.pepsico.com', career_page_url: 'https://www.pepsicojobs.com', industry: 'Food & Beverages', size: 'Enterprise', location: 'Purchase, NY', category: 'Food & Beverage' },
  { name: 'Walmart', website: 'https://www.walmart.com', career_page_url: 'https://careers.walmart.com', industry: 'Retail', size: 'Enterprise', location: 'Bentonville, AR', category: 'Retail' },
  { name: 'Target', website: 'https://www.target.com', career_page_url: 'https://corporate.target.com/careers', industry: 'Retail', size: 'Enterprise', location: 'Minneapolis, MN', category: 'Retail' },
  { name: 'Costco', website: 'https://www.costco.com', career_page_url: 'https://www.costco.com/jobs.html', industry: 'Retail', size: 'Enterprise', location: 'Issaquah, WA', category: 'Retail' },
];

export default Companies;
